import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import { Save, Cancel, Warning } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { storyService, eventsService } from '../services/api';
import ProfanityAwareTextField from '../components/ProfanityAwareTextField';
import { validateTextForProfanity } from '../utils/profanityFilter';

const EditStory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Environment variable controls
  const makeAllPrivate = true; //process.env.REACT_APP_MAKE_ALL_PRIVATE === 'true';
  const mustNeedEvent = true; //process.env.REACT_APP_MUST_NEED_EVENT === 'true';
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    status: 'draft',
    visibility: makeAllPrivate ? 'private' : 'private',
    tags: [],
    event_id: ''
  });
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  
  // Word count state
  const [wordCount, setWordCount] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Calculate word count
  const calculateWordCount = (text) => {
    if (!text || !text.trim()) return 0;
    return text.trim().split(/\s+/).length;
  };

  // Get word count validation for selected event
  const getWordCountValidation = () => {
    if (!selectedEvent || !selectedEvent.min_word_count && !selectedEvent.max_word_count) {
      return { valid: true, message: '' };
    }

    const count = wordCount;
    
    if (selectedEvent.min_word_count && count < selectedEvent.min_word_count) {
      return {
        valid: false,
        message: `Story must have at least ${selectedEvent.min_word_count} words. Current: ${count} words.`
      };
    }
    
    if (selectedEvent.max_word_count && count > selectedEvent.max_word_count) {
      return {
        valid: false,
        message: `Story must not exceed ${selectedEvent.max_word_count} words. Current: ${count} words.`
      };
    }
    
    return {
      valid: true,
      message: `Word count: ${count} words`
    };
  };

  useEffect(() => {
    const fetchStory = async () => {
      try {
        setLoading(true);
        
        const response = await storyService.getStory(id);
        
        // Check if the response has the nested structure
        const story = response.data?.data || response.data;
        
        // Check if user can edit this story (fix: use userId instead of user_id)
        if (story.author_id !== user?.userId && user?.role !== 'admin') {
          setError('You do not have permission to edit this story');
          return;
        }

        const newFormData = {
          title: story.title || '',
          content: story.content || '',
          status: story.status || 'draft',
          visibility: makeAllPrivate ? 'private' : (story.visibility || 'private'),
          tags: story.tags || [],
          event_id: story.event_id || ''
        };
        
        setFormData(newFormData);
        
        // Initialize word count and selected event
        setWordCount(calculateWordCount(newFormData.content));
        if (newFormData.event_id) {
          // We'll set the selected event after events are loaded
        }
      } catch (err) {
        setError('Failed to load story');
        console.error('Error fetching story:', err);
      } finally {
        setLoading(false);
      }
    };

    const loadEvents = async () => {
      try {
        setEventsLoading(true);
        
        // Get all events first
        const response = await eventsService.getEvents();
        const allEvents = response.data.data.events || [];
        
        // Filter to only show active events where the user is participating
        const participatingEvents = allEvents.filter(event => 
          event.status === 'active' && event.user_participating === true
        );
        
        // If the current story has an event_id, make sure it's included in the list
        // even if it doesn't meet the normal filter criteria (e.g., event ended)
        if (formData.event_id) {
          const currentEvent = allEvents.find(event => event.event_id === formData.event_id);
          if (currentEvent && !participatingEvents.find(e => e.event_id === formData.event_id)) {
            // Add the current event to the list so user can see it and change it
            participatingEvents.unshift(currentEvent);
          }
        }
        
        setEvents(participatingEvents);
      } catch (err) {
        console.error('Error loading events:', err);
        // Don't show error for events loading failure, it's optional
      } finally {
        setEventsLoading(false);
      }
    };

    if (id && user) {
      fetchStory();
      loadEvents();
    }
  }, [id, user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Update word count when content changes
    if (name === 'content') {
      setWordCount(calculateWordCount(value));
    }

    // Update selected event when event_id changes
    if (name === 'event_id') {
      const event = events.find(e => e.event_id === value);
      setSelectedEvent(event || null);
    }
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!formData.tags.includes(tagInput.trim())) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, tagInput.trim()]
        }));
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      setError('Title and content are required');
      return;
    }

    // Check if event is required
    if (mustNeedEvent && !formData.event_id) {
      setError('You must link this story to an event');
      return;
    }

    // Check word count validation for selected event
    const wordCountValidation = getWordCountValidation();
    if (!wordCountValidation.valid) {
      setError(wordCountValidation.message);
      return;
    }

    // Check for profanity before submitting
    const titleValidation = validateTextForProfanity(formData.title);
    const contentValidation = validateTextForProfanity(formData.content);
    
    if (titleValidation.hasProfanity || contentValidation.hasProfanity) {
      const profaneWords = [...titleValidation.profaneWords, ...contentValidation.profaneWords];
      setError(`Please remove inappropriate language before saving: ${profaneWords.join(', ')}`);
      return;
    }

    // Check tags for profanity
    for (const tag of formData.tags) {
      const tagValidation = validateTextForProfanity(tag);
      if (tagValidation.hasProfanity) {
        setError(`Tag "${tag}" contains inappropriate language: ${tagValidation.profaneWords.join(', ')}`);
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);
      await storyService.updateStory(id, formData);
      navigate(`/story/${id}`);
    } catch (err) {
      setError('Failed to update story');
      console.error('Error updating story:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/story/${id}`);
  };

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">Please log in to edit stories.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/stories')} sx={{ mt: 2 }}>
          Back to Stories
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Edit Story
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <ProfanityAwareTextField
            fullWidth
            label="Story Title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            margin="normal"
            required
            variant="outlined"
            placeholder="Enter your story title..."
            showProfanityWarning={true}
            showCleanSuggestion={true}
          />

          <ProfanityAwareTextField
            fullWidth
            label="Story Content"
            name="content"
            value={formData.content}
            onChange={handleInputChange}
            margin="normal"
            required
            multiline
            rows={12}
            variant="outlined"
            placeholder="Write your story here..."
            showProfanityWarning={true}
            showCleanSuggestion={true}
          />

          {/* Word Count Display */}
          <Box sx={{ mt: 1, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Word count: {wordCount}
            </Typography>
            {selectedEvent && (selectedEvent.min_word_count || selectedEvent.max_word_count) && (
              <Typography 
                variant="body2" 
                color={getWordCountValidation().valid ? 'success.main' : 'error.main'}
              >
                {selectedEvent.min_word_count && selectedEvent.max_word_count 
                  ? `Required: ${selectedEvent.min_word_count} - ${selectedEvent.max_word_count} words`
                  : selectedEvent.min_word_count 
                    ? `Minimum: ${selectedEvent.min_word_count} words`
                    : `Maximum: ${selectedEvent.max_word_count} words`
                }
              </Typography>
            )}
          </Box>

          <Box sx={{ mt: 2, mb: 2 }}>
            <ProfanityAwareTextField
              fullWidth
              label="Add Tags (press Enter to add)"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={handleAddTag}
              variant="outlined"
              helperText="Press Enter to add a tag"
              showProfanityWarning={true}
              showCleanSuggestion={false}
            />
            <Box sx={{ mt: 1 }}>
              {formData.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  onDelete={() => handleRemoveTag(tag)}
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          </Box>

          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              label="Status"
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="published">Published</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Visibility</InputLabel>
            <Select
              name="visibility"
              value={formData.visibility}
              onChange={handleInputChange}
              label="Visibility"
              disabled={makeAllPrivate}
            >
              <MenuItem value="private">Private</MenuItem>
              {!makeAllPrivate && <MenuItem value="public">Public</MenuItem>}
            </Select>
            {makeAllPrivate && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                All stories are set to private in this environment
              </Typography>
            )}
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>{mustNeedEvent ? 'Link to Event (Required)' : 'Link to Event (Optional)'}</InputLabel>
            <Select
              name="event_id"
              value={formData.event_id}
              onChange={handleInputChange}
              label={mustNeedEvent ? 'Link to Event (Required)' : 'Link to Event (Optional)'}
              disabled={eventsLoading}
              required={mustNeedEvent}
            >
              {!mustNeedEvent && (
                <MenuItem value="">
                  <em>No Event</em>
                </MenuItem>
              )}
              {events.map((event) => (
                <MenuItem key={event.event_id} value={event.event_id}>
                  {event.title} ({new Date(event.start_date).toLocaleDateString()})
                </MenuItem>
              ))}
            </Select>
            {eventsLoading && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                Loading events...
              </Typography>
            )}
            {mustNeedEvent && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                Stories must be linked to an event in this environment
              </Typography>
            )}
          </FormControl>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<Save />}
              disabled={saving}
              sx={{ flex: 1 }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default EditStory;
